import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import classnames from "classnames";
import { FocusTrap } from "@ark-ui/react";

import Popover from "@/components/popover/popover";
import Button from "@/components/button/button";
import { useDashboardData } from "@/features/shared/hooks";
import IconAvatar from "@/components/icon-avatar/icon-avatar";
import useLayoutStore from "@/store/useLayoutStore";

import styles from "./navigation.module.css";

interface NavigationDashboardSelectorProps {
  setCollapsed: (collapse?: boolean) => void;
}

export default function NavigationDashboardSelector({
  setCollapsed,
}: NavigationDashboardSelectorProps) {
  const { allDashboards, selectedDashboard } = useDashboardData();
  const location = useLocation();
  const [, layoutStore] = useLayoutStore();

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const [isOpen, setOpen] = useState(false);
  const handleOpen = useCallback(
    (open: boolean) => {
      setOpen(open);
      if (open) {
        setCollapsed(false);
        layoutStore.trigger.setFullscreen({ value: false });
      }
    },
    [setOpen, setCollapsed, layoutStore],
  );

  if (!allDashboards || !Array.isArray(allDashboards) || allDashboards.length === 0)
    return null;

  return (
    <Popover
      rootProps={{
        open: isOpen,
        onOpenChange: (details) => handleOpen(details.open),
        positioning: {
          placement: "bottom",
          sameWidth: true,
        },
      }}
      trigger={(context) => (
        <div
          className={classnames(
            styles.navigationItemWrapper,
            styles.navigationDashboardWrapper,
            {
              [styles.navigationDashboardWrapperActive]: context.open,
            },
          )}
        >
          <Button variant="ghost" className={styles.navigationItem}>
            <IconAvatar icon="layout-dashboard" />
            <div className={styles.navigationLabel}>
              {selectedDashboard?.name ?? "Dashboard"}
            </div>
          </Button>
        </div>
      )}
    >
      <FocusTrap disabled={!isOpen}>
        <div className={styles.navigationDashboardPopover}>
          {allDashboards.map((dashboard) => (
            <Link
              key={dashboard.id}
              to={dashboard.slug}
              tabIndex={0}
              className={classnames(styles.navigationDashboardLink, {
                [styles.navigationDashboardLinkActive]:
                  dashboard.id === selectedDashboard?.id,
              })}
            >
              {dashboard.name}
            </Link>
          ))}
        </div>
      </FocusTrap>
    </Popover>
  );
}
